import { rms, smooth } from './pcm'

/* The orb, ported from the phone-verified prototype
   (ConvoAgent websocket_client/table.html @ cab5ea1, lines 279-591).

   A twisted pencil of ellipses. One circle, drawn ten times; each copy is
   squashed along an axis, rotated further than the last, and shifted along that
   axis by its own amount -- so the copies are not concentric, and the fan they
   make is lopsided instead of mechanical. Each copy also breathes its own
   radius, crossing in and out through the white circle. The axis turns, so the
   fan travels around the ring.

   Every number below was tuned in websocket_client/ring_ab.html, which has a
   slider per parameter and a simulated conversation to watch them under. Change
   them there and carry the result across. Guessing them here is a bad
   afternoon. */

interface OrbConfig {
  spin: number
  offset: number
  inout: number
  glow: number
  waveAmp: number
  waveThick: number
  waveSpeed: number
}

/** Strand count, squash and twist are identical in every state — the shape is
    settled and only its energy changes, so a state change never rebuilds the
    per-strand table below. */
const N = 10
const SQUASH = 0.95
const TWIST = 90

const IDLE: OrbConfig = {
  spin: 30, offset: 0, inout: 0, glow: 0.058,
  waveAmp: 0.010, waveThick: 0.043, waveSpeed: 0.21,
}
const PRESETS: Record<OrbState, OrbConfig> = {
  idle: IDLE,
  listening: {
    spin: 15, offset: 0.015, inout: 0.005, glow: 0.035,
    waveAmp: 0.076, waveThick: 0.069, waveSpeed: 0.86,
  },
  speaking: {
    spin: 7, offset: 0.030, inout: 0.020, glow: 0.120,
    waveAmp: 0.350, waveThick: 0.120, waveSpeed: 2.00,
  },
}

export type OrbState = 'idle' | 'listening' | 'speaking'

const EASED = ['spin', 'offset', 'inout', 'glow', 'waveAmp', 'waveThick', 'waveSpeed'] as const
const RANK: Record<OrbState, number> = { idle: 0, listening: 1, speaking: 2 }
const TAU = Math.PI * 2

// Colour runs ALONG each strand: indigo where it leaves the white circle,
// orange where it comes back. Measured off the reference clip. First and last
// stop match so the conic gradient is seamless.
const STOPS: [number, string][] = [
  [0.00, 'rgb(76 29 149)'], [0.14, 'rgb(124 58 237)'], [0.30, 'rgb(192 38 211)'],
  [0.46, 'rgb(255 45 149)'], [0.62, 'rgb(255 138 0)'], [0.74, 'rgb(255 217 168)'],
  [0.88, 'rgb(56 189 248)'], [1.00, 'rgb(76 29 149)'],
]

/* Per-strand constants. Deliberately not a uniform ladder: the golden angle
   spreads the phases so no two strands line up for long, which is the whole
   difference between alive and mechanical. Immutable, so it is built once for
   the module rather than per instance. */
const STRANDS = Array.from({ length: N }, (_, idx) => {
  const i = idx + 1
  const f = i / N                          // 0 = at the ring, 1 = innermost
  const g = (i * 2.39996323) % TAU         // golden angle
  return {
    f,
    sq: 1 - (1 - SQUASH) * f,
    rate: 1 / (1 + i * 0.035),             // its own axis speed -> drift
    tw: (i * TWIST * Math.PI) / 180,
    offMul: (0.35 + 0.65 * Math.abs(Math.sin(g * 3.7))) * f,
    offPer: 5.5 + (i % 4) * 2.3,
    ioMul: 0.4 + 0.6 * Math.abs(Math.cos(g * 2.1)),
    ioPer: 6.5 + (i % 5) * 1.9,
    phase: g,
    hue: (i * 31 * Math.PI) / 180,         // where its gradient starts
  }
})

/* The inner waveform: five ribbons across the middle of the white circle.

   A ribbon is FILLED, not stroked — its half-width tapers with the same
   envelope as its amplitude, and that taper is what makes the lens-shaped
   crossings. A stroked line of constant width cannot make that shape.

   env(u) = sin(PI*u)^1.2 is zero at both ends, so every ribbon lands on the
   centreline at the edges however loud it gets and no end pokes through the
   ring. Each ribbon owns a frequency band: bass swells the fat slow one,
   sibilance flicks the thin fast ones, which is what makes it read as the voice
   rather than as decoration that happens to be moving. */
const WAVE = [
  // k = half-cycles across the width. rate = phase drift, sign = direction.
  // shift = where its colour ramp starts. w = its share of amplitude/width.
  { k: 1.0, rate: 0.55, shift: 0.00, band: 0, w: 1.00 },
  { k: 1.5, rate: -0.80, shift: 0.22, band: 1, w: 0.78 },
  { k: 2.0, rate: 0.95, shift: 0.45, band: 2, w: 0.58 },
  { k: 2.5, rate: -1.25, shift: 0.68, band: 3, w: 0.44 },
  { k: 3.5, rate: 1.60, shift: 0.86, band: 4, w: 0.30 },
]
const WAVE_SEG = 72
const SEG = 96

/* Bin width is sampleRate/512 — about 94Hz at the usual 48k — so these edges
   cover roughly 90Hz to 6kHz: the fundamental through the sibilance, which is
   all speech puts anywhere. Log-ish spaced because hearing is; a linear split
   leaves the top three ribbons reading the same near-silence. */
const BAND_EDGES = [1, 4, 9, 18, 34, 64]

/** Levels and bands are read from whichever of these is live. */
export interface OrbAnalysers {
  mic: AnalyserNode
  bot: AnalyserNode
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)

/**
 * Starts the animation. Returns a teardown.
 *
 * `getAnalysers` is polled rather than passed once: the orb runs on the
 * "tap to start" screen before any audio exists, and must pick the analysers up
 * when a session begins without being restarted.
 */
export function startOrb(
  canvas: HTMLCanvasElement,
  getAnalysers: () => OrbAnalysers | null,
): () => void {
  // The strands are stroked once into the offscreen canvas, then laid onto the
  // visible one twice: blurred for the halo, then crisp on top. Every strand
  // gets a real glow along its whole length for ONE blur per frame instead of
  // one per strand.
  const fx = document.createElement('canvas')
  const visible = canvas.getContext('2d')
  const offscreen = fx.getContext('2d')
  // jsdom has no canvas, and a browser can refuse a context under memory
  // pressure. Either way there is nothing to animate.
  if (!visible || !offscreen) return () => {}

  // Bound to a non-nullable type once, rather than asserting at each of the
  // forty-odd uses below: TypeScript will not carry a narrowing into the
  // closures that do the drawing.
  const ctx: CanvasRenderingContext2D = visible
  const fxc: CanvasRenderingContext2D = offscreen

  // All of this is per-instance. As module state a remount would inherit a
  // stale spin angle and the orb would jump on the second mount.
  const cfg: OrbConfig = { ...IDLE }
  const bands = new Float32Array(WAVE.length)
  const freq = new Uint8Array(256)            // fftSize 512 -> 256 bins
  let axAcc = 0
  let waveAcc = 0
  let state: OrbState = 'idle'
  let lvl = 0
  let micLevel = 0
  let botLevel = 0

  function conic(cx: number, cy: number, from: number): CanvasGradient {
    // Colour as a function of angle around a point, which for a closed loop is
    // colour along the path. One stroke per strand instead of one per segment.
    const g = ctx.createConicGradient(from, cx, cy)
    for (const [o, c] of STOPS) g.addColorStop(o, c)
    return g
  }

  function strandPath(
    g: CanvasRenderingContext2D,
    cx: number, cy: number, R: number,
    s: (typeof STRANDS)[number], ax: number, off: number, t: number,
  ): void {
    const ca = Math.cos(ax)
    const sa = Math.sin(ax)
    g.beginPath()
    for (let k = 0; k <= SEG; k++) {
      const u = (k / SEG) * TAU
      const warp = 1 + 0.025 * Math.sin(3 * u + t * 0.6 + s.phase)
      const x = Math.cos(u) * R * s.sq * warp + off
      const y = Math.sin(u) * R * warp
      const X = cx + x * ca - y * sa
      const Y = cy + x * sa + y * ca
      if (k) g.lineTo(X, Y)
      else g.moveTo(X, Y)
    }
    g.closePath()
  }

  function waveGrad(
    g: CanvasRenderingContext2D,
    x0: number, x1: number, y: number, shift: number,
  ): CanvasGradient {
    const lg = g.createLinearGradient(x0, y, x1, y)
    // (o + shift) % 1 can produce out-of-order offsets, which is intended — it
    // wraps the ramp. addColorStop does not require sorted input.
    for (const [o, c] of STOPS) lg.addColorStop((o + shift) % 1, c)
    return lg
  }

  function drawWave(
    g: CanvasRenderingContext2D,
    cx: number, cy: number, R: number, level: number,
  ): void {
    const halfW = R * 0.92
    g.save()
    // Clipped to the circle: a loud band must never punch through the ring.
    g.beginPath()
    g.arc(cx, cy, R * 0.9, 0, TAU)
    g.clip()
    for (const s of WAVE) {
      const b = bands[s.band]
      const amp = R * cfg.waveAmp * s.w * (0.30 + 1.2 * b)
      const th = Math.max(0.6, R * cfg.waveThick * s.w * (0.6 + 0.8 * b))
      const ph = waveAcc * s.rate
      g.beginPath()
      for (let i = 0; i <= WAVE_SEG; i++) {
        const u = i / WAVE_SEG
        const e = Math.pow(Math.sin(Math.PI * u), 1.2)
        const x = cx + (u * 2 - 1) * halfW
        const y = cy + amp * e * Math.sin(s.k * Math.PI * u + ph)
        if (i) g.lineTo(x, y - th * e)
        else g.moveTo(x, y - th * e)
      }
      for (let i = WAVE_SEG; i >= 0; i--) {
        const u = i / WAVE_SEG
        const e = Math.pow(Math.sin(Math.PI * u), 1.2)
        g.lineTo(
          cx + (u * 2 - 1) * halfW,
          cy + amp * e * Math.sin(s.k * Math.PI * u + ph) + th * e,
        )
      }
      g.closePath()
      g.fillStyle = waveGrad(g, cx - halfW, cx + halfW, cy, s.shift)
      g.globalAlpha = clamp01((0.75 - s.w * 0.15) * (0.5 + level * 0.8))
      g.fill()
    }
    g.restore()
  }

  function draw(t: number, dt: number, level: number): void {
    // ACCUMULATED, never t * speed(level): multiplying time by a level-dependent
    // speed makes the phase shrink the moment the level drops, which runs the
    // whole pencil backwards. Integrating can only go forward.
    axAcc += dt * (TAU / cfg.spin) * (1 + level * 1.4)
    waveAcc += dt * cfg.waveSpeed * TAU * 0.25

    // 1.5x, not the device's full ratio: a 3x phone renders nine times the
    // pixels for no visible gain at 300px, and a phone that throttles here
    // starts dropping the audio callbacks that feed the agent.
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
    const w = Math.round(canvas.clientWidth * dpr)
    const h = Math.round(canvas.clientHeight * dpr)
    if (!w || !h) return
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h }
    if (fx.width !== w || fx.height !== h) { fx.width = w; fx.height = h }
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, w, h)
    fxc.setTransform(1, 0, 0, 1, 0, 0)
    fxc.clearRect(0, 0, w, h)

    const cx = w / 2
    const cy = h / 2
    const size = Math.min(w, h)
    // Breathing, measured off the clip: ~±3% over 4.4s, width and height out of
    // phase, so it wobbles rather than pulsing. Uniform scale reads as a
    // heartbeat; this reads as breathing.
    const ph = (t % 4.4) / 4.4
    const bx = 1 + 0.03 * Math.sin(ph * TAU)
    const by = 1 + 0.03 * Math.sin(ph * TAU + 2.1)
    // The coefficients are deliberately large: at 300px across a table a 7%
    // swell is invisible. The squared term makes a loud syllable bloom much
    // harder than a quiet one, instead of everything sitting at a polite grey.
    const L = level
    const L2 = level * level
    const R = size * 0.32 * (1 + L * 0.18)
    const fan = 0.45 + L * 1.4                              // level widens the fan
    const open = 0.775 + 0.225 * Math.cos((t / 9) * TAU)    // fan opens and closes

    fxc.save()
    fxc.translate(cx, cy); fxc.scale(bx, by); fxc.translate(-cx, -cy)
    fxc.globalCompositeOperation = 'lighter'   // strands add where they pile up
    fxc.lineJoin = 'round'
    for (const s of STRANDS) {
      const ax = axAcc * s.rate + s.tw
      const rr = R * (1 + cfg.inout * fan * s.ioMul * Math.sin((t / s.ioPer) * TAU + s.phase))
      const off = R * cfg.offset * fan * s.offMul * open * Math.sin((t / s.offPer) * TAU + s.phase)
      strandPath(fxc, cx, cy, rr, s, ax, off, t)
      // Centred on the strand's own centre, not the orb's, or the colours slide
      // off the line once it is displaced.
      fxc.strokeStyle = conic(cx + off * Math.cos(ax), cy + off * Math.sin(ax), ax + s.hue)
      fxc.globalAlpha = clamp01((0.88 - s.f * 0.33) * (0.55 + L * 0.85))
      fxc.lineWidth = Math.max(1, (0.013 + s.f * 0.006) * R * (1 + L * 0.9))
      fxc.stroke()
    }
    // Into the SAME offscreen canvas, so the waveform inherits the blur-and-crisp
    // glow below for free — no second blur pass.
    drawWave(fxc, cx, cy, R, L)
    fxc.restore()

    ctx.globalCompositeOperation = 'lighter'
    ctx.filter = `blur(${(cfg.glow * R * (1 + L2 * 1.1)).toFixed(2)}px)`
    ctx.globalAlpha = clamp01(0.35 + L * 0.55 + L2 * 0.35)
    ctx.drawImage(fx, 0, 0)
    ctx.filter = 'none'
    ctx.globalAlpha = 1
    ctx.drawImage(fx, 0, 0)

    // The white ring, stroked three times over the same arc. Still under
    // 'lighter' from above, which is what makes the centre read white-hot.
    ctx.save()
    ctx.translate(cx, cy); ctx.scale(bx, by); ctx.translate(-cx, -cy)
    ctx.beginPath()
    ctx.arc(cx, cy, R, 0, TAU)
    ctx.filter = `blur(${(cfg.glow * R * (0.8 + L2 * 0.9)).toFixed(2)}px)`
    ctx.lineWidth = 0.03 * R * (1 + L * 0.7)
    ctx.strokeStyle = 'rgba(205,225,255,.75)'; ctx.stroke()
    ctx.filter = 'none'
    ctx.lineWidth = 0.026 * R; ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.stroke()
    ctx.lineWidth = 0.013 * R; ctx.strokeStyle = '#fff'; ctx.stroke()
    ctx.restore()
  }

  /* Idle is simply both levels sitting near zero — there is no third signal.
     The agent's own voice wins when both are up, because the socket path mutes
     the mic while it talks. */
  function pickState(): OrbState {
    if (botLevel > 0.08) return 'speaking'
    if (micLevel > 0.08) return 'listening'
    return 'idle'
  }

  function readBands(): void {
    const a = getAnalysers()
    const an = !a ? null : botLevel > 0.08 ? a.bot : micLevel > 0.08 ? a.mic : null
    if (!an) {
      // Nobody talking, or not connected. Decay rather than snap to zero.
      for (let i = 0; i < bands.length; i++) bands[i] -= bands[i] * 0.10
      return
    }
    an.getByteFrequencyData(freq)
    for (let i = 0; i < bands.length; i++) {
      const lo = BAND_EDGES[i]
      const hi = BAND_EDGES[i + 1]
      let sum = 0
      for (let k = lo; k < hi; k++) sum += freq[k]
      const target = Math.min(1, sum / (hi - lo) / 140)
      // Same fast-attack / slow-release as everything else. Without it the
      // ribbons twitch every frame instead of moving like a voice.
      bands[i] += (target - bands[i]) * (target > bands[i] ? 0.35 : 0.10)
    }
  }

  // Mic and bot read the same way on the same cadence. The prototype sampled
  // the mic per capture block (~11.7Hz) and the bot on this interval; taking
  // both from their analysers keeps the maths and drops the asymmetry.
  const levelBuf = new Float32Array(512)
  const levelTimer = setInterval(() => {
    const a = getAnalysers()
    if (!a) {
      micLevel = smooth(micLevel, 0)
      botLevel = smooth(botLevel, 0)
      return
    }
    a.mic.getFloatTimeDomainData(levelBuf)
    micLevel = smooth(micLevel, Math.min(1, rms(levelBuf) * 6))
    a.bot.getFloatTimeDomainData(levelBuf)
    botLevel = smooth(botLevel, Math.min(1, rms(levelBuf) * 6))
  }, 40)

  let raf = 0
  let last = performance.now()
  const t0 = last

  function frame(now: number): void {
    // Clamped: a backgrounded tab must not fast-forward the animation.
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now

    const target = pickState()
    // Frame-rate independent easing, fast up the rank and slow back down —
    // snapping the spin from 30s to 7s the instant the agent starts talking is
    // a visible lurch.
    const k = 1 - Math.exp(-dt / (RANK[target] > RANK[state] ? 0.28 : 1.2))
    state = target
    for (const p of EASED) cfg[p] += (PRESETS[target][p] - cfg[p]) * k

    readBands()

    // One more lerp on top of smooth()'s attack and release, so a long frame
    // cannot make the ring jump.
    lvl += (Math.max(micLevel, botLevel) - lvl) * 0.12

    if (!document.hidden) draw((now - t0) / 1000, dt, lvl)
    raf = requestAnimationFrame(frame)
  }

  raf = requestAnimationFrame(frame)

  return () => {
    cancelAnimationFrame(raf)
    clearInterval(levelTimer)
  }
}
